// Dependencies

import React, { Component } from 'react';

import classnames from 'classnames';
import truncateHtml from 'html-truncate';
// eslint-disable-next-line lodash/import-scope
import { map, snakeCase, has } from 'lodash';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import Lazy from 'react-lazyload';

// Utils
import { Type } from '@nerdwallet/currency';
import { Tracking } from '@nerdwallet/nw-client-lib';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';

// CSS
require('./vertical-tabs.less');

const { formInputChanged } = Tracking;
const PREVIEW_LENGTH = 350;

class VerticalTabs extends Component {
  static jsxToHtml(content, maxLen = PREVIEW_LENGTH) {
    return truncateHtml(renderToStaticMarkup(content), maxLen);
  }

  constructor(props) {
    super(props);

    this.state = {
      activeTab: 0,
      seeAll: [],
    };
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (has(nextProps, 'activeTab')) {
      this.setState({
        activeTab: nextProps.activeTab,
      });
    }
  }

  switchTab(tabIndex, trackingFieldName) {
    return () => {
      if (trackingFieldName) {
        formInputChanged({
          formName: this.props.trackingFormName,
          fieldName: trackingFieldName,
        });
      }

      this.setState({
        activeTab: tabIndex,
      });

      if (this.props.onClick) {
        this.props.onClick(tabIndex);
      }
    };
  }

  seeAll(tabIndex, trackingFieldName) {
    return () => {
      if (trackingFieldName) {
        formInputChanged({
          formName: this.props.trackingFormName,
          fieldName: `${trackingFieldName}_see_all`,
        });
      }

      // eslint-disable-next-line react/no-access-state-in-setstate -- directive added automatically by Shepherd migration
      const newSeeAllState = this.state.seeAll.slice();
      newSeeAllState[tabIndex] = true;

      this.setState({
        seeAll: newSeeAllState,
      });
    };
  }

  renderHeader() {
    const { header, headerIconUrl } = this.props;

    return (
      <div className="vertical-tabs-header">
        <Lazy>
          <div className="vertical-tabs-header-icon">
            <img alt="" src={headerIconUrl} />
          </div>
        </Lazy>
        <div className="vertical-tabs-header-title">
          <Type component="h2" size={3} style={{ lineHeight: 'inherit' }} bold>
            {header}
          </Type>
        </div>
      </div>
    );
  }

  renderTabs() {
    const tabs = this.props.tabs;
    const tabsOutput = [];
    const output = [];

    // eslint-disable-next-line lodash/collection-method-value, lodash/collection-return -- directive added automatically by Shepherd migration
    map(tabs, (tab, index) => {
      const content = tab.content || <div />;
      const full = VerticalTabs.jsxToHtml(content, Number.MAX_SAFE_INTEGER);
      const preview = VerticalTabs.jsxToHtml(content);
      const diff = full.length !== preview.length;
      const last = index === tabs.length - 1;
      const key = `${index}_${snakeCase(tab.header)}`;

      const tabClasses = classnames('vertical-tabs-wrapper-tabs-tab', {
        active: index === this.state.activeTab,
        last,
      });

      const contentClasses = classnames('vertical-tabs-wrapper-content', {
        last,
      });

      const previewContentClasses = classnames(
        'vertical-tabs-wrapper-content-preview',
        {
          'mobile-show': diff && !this.state.seeAll[index],
        }
      );

      const fullContentClasses = classnames(
        'vertical-tabs-wrapper-content-full',
        {
          'mobile-show': !diff || this.state.seeAll[index],
          'desktop-show': index === this.state.activeTab,
        }
      );

      tabsOutput.push(
        <div
          key={`${key}_tab`}
          className={tabClasses}
          onClick={this.switchTab(index, tab.trackingFieldName)}
        >
          <Type
            component="h3"
            size={2}
            bold
            className="vertical-tabs-wrapper-tabs-tab-header"
          >
            {tab.header}
          </Type>
        </div>
      );

      /* eslint-disable react/no-danger */
      output.push(
        <div key={`${key}_header`} className="vertical-tabs-wrapper-header">
          <Type
            component="h3"
            size={2}
            bold
            className="vertical-tabs-wrapper-tabs-tab-header"
          >
            {tab.header}
          </Type>
        </div>,
        <div key={`${key}_content`} className={contentClasses}>
          <div className={fullContentClasses}>{content}</div>

          <div className={previewContentClasses}>
            <div dangerouslySetInnerHTML={{ __html: preview }} />
            <div
              className="vertical-tabs-wrapper-content-preview-see-all"
              onClick={this.seeAll(index, tab.trackingFieldName)}
            >
              See all
            </div>
          </div>
        </div>
      );
      /* eslint-enable react/no-danger */
    });

    return (
      <div className="vertical-tabs-wrapper">
        <div className="vertical-tabs-wrapper-tabs">{tabsOutput}</div>
        {output}
      </div>
    );
  }

  renderImpressionTracking() {
    const { trackingFormName, impressionTracking } = this.props;

    if (!impressionTracking) {
      return null;
    }

    return (
      <ElementImpression
        id={`mortgages-${trackingFormName}`}
        elementName={trackingFormName}
      >
        <div />
      </ElementImpression>
    );
  }

  render() {
    const containerClasses = {};

    if (this.props.className) {
      containerClasses[this.props.className] = true;
    }

    return (
      <div className={classnames('vertical-tabs', containerClasses)}>
        {this.renderHeader()}
        {this.renderTabs()}
        {this.renderImpressionTracking()}
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
VerticalTabs.displayName = 'VerticalTabs';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
VerticalTabs.defaultProps = {
  activeTab: 0,
  tabs: [],
  className: null,
  header: null,
  impressionTracking: false,
  onClick: () => {},
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
VerticalTabs.propTypes = {
  activeTab: PropTypes.number,
  className: PropTypes.string,
  header: PropTypes.string,
  headerIconUrl: PropTypes.string.isRequired,
  impressionTracking: PropTypes.bool,
  onClick: PropTypes.func,
  tabs: PropTypes.array,
  trackingFormName: PropTypes.string.isRequired,
};

export default VerticalTabs;
